import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import { useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes, LocalStorage } from '../../helpers'
import { APPLICATION_VERSION, axiosInstance } from '../../helpers/axios'
import { useAppDispatch } from '../../hooks'
import { setRootUser, fetchLeasingContext } from '../../store/User/action'
import { Visibility, VisibilityOff } from '@mui/icons-material'

export const Login = () => {
    const location = useLocation()
    const inviteToken = useMemo(
        () => new URLSearchParams(location.search).get('invite'),
        [location.search],
    )
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [email, setEmail] = useState('')
    const [doValidate, setDoValidate] = useState(false)
    const [loginError, setLoginError] = useState('')
    const [propertyName, setPropertyName] = useState('')
    const [emailLocked, setEmailLocked] = useState(false)

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        if (!inviteToken) {
            return
        }

        localStorage.setItem(LocalStorage.PENDING_INVITE_TOKEN, inviteToken)

        axiosInstance
            .get(`outreach/lead-invitations/${inviteToken}/`)
            .then((res) => {
                if (res.data.email) {
                    setEmail(res.data.email)
                    setEmailLocked(true)
                }
                setPropertyName(res.data.property_name || '')
            })
            .catch(() => {
                setLoginError('This invitation link is invalid or has expired.')
            })
    }, [inviteToken])

    const handleLogin = () => {
        setLoginError('')
        axiosInstance
            .post('user/login/', {
                email: email,
                password: password,
            })
            .then(async (res) => {
                localStorage.setItem(
                    LocalStorage.EZTURN_TENANT_TOKEN,
                    res.data.token,
                )
                axiosInstance.defaults.headers.common[
                    'Authorization'
                ] = `token ${res.data.token}`
                dispatch(setRootUser(res.data))

                if (inviteToken) {
                    try {
                        await axiosInstance.post(
                            `outreach/lead-invitations/${inviteToken}/accept/`,
                            { email, password },
                        )
                        localStorage.removeItem(LocalStorage.PENDING_INVITE_TOKEN)
                    } catch {
                        setLoginError(
                            'Signed in, but could not link this invitation to your account.',
                        )
                    }
                }

                await dispatch(fetchLeasingContext())
                navigate(AppRoutes.home)
            })
            .catch(() => {
                setDoValidate(true)
                setLoginError('Incorrect email or password')
            })
    }

    const resetPasswordHref = () => {
        const params = new URLSearchParams()
        if (inviteToken) {
            params.set('invite', inviteToken)
        }
        if (email) {
            params.set('email', email)
        }
        const query = params.toString()
        return query
            ? `${AppRoutes.resetPassword}?${query}`
            : AppRoutes.resetPassword
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f5f5f5',
            }}
        >
            <Container maxWidth="sm" sx={{ flex: 1, display: 'flex' }}>
                <Paper
                    elevation={3}
                    sx={{
                        width: '100%',
                        my: { xs: 2, sm: 4 },
                        p: { xs: 2, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'center',
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                    >
                        Welcome Back
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {propertyName
                            ? `Sign in to apply at ${propertyName}`
                            : 'Please sign in to continue'}
                    </Typography>

                    <Box component="form" sx={{ mt: 3 }}>
                        <TextField
                            fullWidth
                            label="Email"
                            error={doValidate && email === ''}
                            variant="outlined"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@domain.com"
                            disabled={emailLocked}
                            sx={{ mb: 3 }}
                        />

                        <OutlinedInput
                            fullWidth
                            error={doValidate && password === ''}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <Visibility />
                                        ) : (
                                            <VisibilityOff />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                            sx={{ mb: 4 }}
                        />

                        {loginError && (
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ mb: 2, textAlign: 'center' }}
                            >
                                {loginError}
                            </Typography>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleLogin}
                            sx={{
                                mb: 2,
                                backgroundColor: '#008C85',
                                '&:hover': {
                                    backgroundColor: '#007c75',
                                },
                            }}
                        >
                            Sign In
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            onClick={() => navigate(resetPasswordHref())}
                            sx={{
                                mb: 2,
                                borderColor: '#008C85',
                                color: '#008C85',
                                '&:hover': {
                                    borderColor: '#007c75',
                                    backgroundColor: 'rgba(0, 140, 133, 0.04)',
                                },
                            }}
                        >
                            Reset Password
                        </Button>
                    </Box>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 'auto', pt: 2 }}
                    >
                        Version {APPLICATION_VERSION}
                    </Typography>
                </Paper>
            </Container>
        </Box>
    )
}
